import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  IconButton,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
// import CallSplitIcon from '@mui/icons-material/CallSplit';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CalculateIcon from '@mui/icons-material/Calculate';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import RecyclingIcon from '@mui/icons-material/Recycling';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';

interface SidebarProps {
  drawerWidth: number;
  mobileOpen: boolean;
  onClose: () => void;
  collapsed?: boolean;
  onCollapseToggle?: () => void;
}

// orgFlag: punktet vises kun når organisationen har det tilvalgt. Bruges til
// features der kun giver mening for en bestemt slags vognmand.
const mainMenuItems: Array<{ key: string; text: string; icon: JSX.Element; path: string; orgFlag?: 'emballageEnabled' }> = [
  { key: 'dashboard', text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { key: 'trailers', text: 'Køretøjer', icon: <LocalShippingIcon />, path: '/trailers' },
  { key: 'calendar', text: 'Kalender', icon: <CalendarMonthIcon />, path: '/calendar' },
  { key: 'koresedler', text: 'Dokumenter', icon: <DescriptionIcon />, path: '/koresedler' },
  // { key: 'routes', text: 'Split', icon: <CallSplitIcon />, path: '/routes' },
  { key: 'arbejdstimer', text: 'Arbejdstimer', icon: <AccessTimeIcon />, path: '/arbejdstimer' },
  { key: 'analytics', text: 'Økonomi', icon: <BarChartIcon />, path: '/analytics' },
  { key: 'prisberegner', text: 'Prisberegner', icon: <CalculateIcon />, path: '/prisberegner' },
  { key: 'tilbud', text: 'Tilbud', icon: <RequestQuoteIcon />, path: '/tilbud' },
  { key: 'produktsalg', text: 'Produktsalg', icon: <StorefrontIcon />, path: '/produktsalg' },
  { key: 'prismotor', text: 'Prismotor', icon: <PriceCheckIcon />, path: '/prismotor' },
  { key: 'ordreskemaer', text: 'Ordreskemaer', icon: <EventRepeatIcon />, path: '/ordreskemaer' },
  { key: 'emballage', text: 'Emballagekompensation', icon: <RecyclingIcon />, path: '/emballage', orgFlag: 'emballageEnabled' as const },
  { key: 'economic', text: 'Bogføring', icon: <AccountBalanceIcon />, path: '/economic' },
  { key: 'omkostninger', text: 'Omkostninger', icon: <MonetizationOnIcon />, path: '/omkostninger' },
  { key: 'settings', text: 'Indstillinger', icon: <SettingsIcon />, path: '/settings' },
  { key: 'brugere', text: 'Brugere', icon: <PeopleIcon />, path: '/brugere' },
];

const adminMenuItems = [
  { text: 'Organisationer', icon: <BusinessIcon />, path: '/admin/organizations' },
  { text: 'Brugere', icon: <PeopleIcon />, path: '/admin/users' },
];

export default function Sidebar({ drawerWidth, mobileOpen, onClose, collapsed = false, onCollapseToggle }: SidebarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  // Query suggested invoice count for badge
  const { data: suggestedInvoices } = useQuery({
    queryKey: ['economic-suggested-count'],
    queryFn: () => api.getEconomicInvoices({ matchStatus: 'SUGGESTED' }),
    refetchInterval: 60000, // Refresh every minute
  });
  const suggestedCount = suggestedInvoices?.length ?? 0;

  const visibleMenuItems = mainMenuItems.filter(item => {
    // Org-flaget gælder også superadmins: er featuren ikke købt, findes den ikke.
    if (item.orgFlag && !user?.organization?.[item.orgFlag]) return false;
    if (isSuperAdmin) return true;
    if (!user?.allowedMenuItems?.length) return true;
    return user.allowedMenuItems.includes(item.key);
  });

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const renderItem = (item: { key?: string; text: string; icon: JSX.Element; path: string }, showBadge: boolean) => {
    const isSelected = location.pathname === item.path;
    const button = (
      <ListItemButton
        selected={isSelected}
        onClick={() => handleNavigation(item.path)}
        sx={{
          borderRadius: 0.5,
          py: 0.75,
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 1 : 1.5,
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'white',
            '& .MuiListItemIcon-root': { color: 'white' },
            '&:hover': { bgcolor: 'primary.dark' },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 'auto' : 32, color: 'inherit', justifyContent: 'center' }}>
          {showBadge && suggestedCount > 0 ? (
            <Badge badgeContent={suggestedCount} color="warning">
              {item.icon}
            </Badge>
          ) : item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
          />
        )}
        {!collapsed && showBadge && suggestedCount > 0 && (
          <Badge badgeContent={suggestedCount} color="warning" sx={{ mr: 1 }} />
        )}
      </ListItemButton>
    );
    return (
      <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
        {collapsed ? <Tooltip title={item.text} placement="right" arrow>{button}</Tooltip> : button}
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        gap: 1,
        minHeight: 48,
      }}>
        {collapsed ? (
          <LocalShippingIcon color="primary" sx={{ fontSize: 24 }} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon color="primary" sx={{ fontSize: 26 }} />
            <Typography variant="subtitle1" fontWeight={700} color="primary" noWrap>
              DORA Insight
            </Typography>
          </Box>
        )}
        {onCollapseToggle && !isMobile && (
          <Tooltip title={collapsed ? 'Udvid menu' : 'Minimér menu'} placement="right" arrow>
            <IconButton size="small" onClick={onCollapseToggle} sx={{ p: 0.5 }}>
              {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider />

      <List sx={{ flex: 1, pt: 0.5, px: 0.5 }}>
        {visibleMenuItems.map(item => renderItem(item, item.key === 'economic'))}
      </List>

      {isSuperAdmin && (
        <>
          <Divider />
          {!collapsed && (
            <Typography
              variant="caption"
              sx={{ px: 1.5, pt: 1.5, pb: 0.5, color: 'text.secondary', fontWeight: 600, fontSize: 10, letterSpacing: 0.5 }}
            >
              ADMINISTRATION
            </Typography>
          )}
          <List sx={{ pb: 1.5, px: 0.5, pt: collapsed ? 0.5 : 0 }}>
            {adminMenuItems.map(item => renderItem(item, false))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: '1px solid',
            borderColor: 'divider',
            transition: 'width 0.2s',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}
